import {
  CSSProperties,
  forwardRef,
  useLayoutEffect,
  useEffect,
  useImperativeHandle,
  useRef,
  RefObject,
  useState,

} from "react";
import { Video, VideoTimestamp } from "../types";
import { useSnackbar } from "notistack";
import "plyr/dist/plyr.css";
import Hls from "hls.js";
import Plyr from "plyr";
import { Box, Typography, CircularProgress, useMediaQuery, useTheme } from "@mui/material";
import { secondsToDurationString, thumbnailUrl } from "../helpers";
import { isSafariBrowser } from "~/constants";
import PlayOverlay from "./PlayOverlay";

export const AspectAwareContainer = forwardRef(function (
  props: {
    video: Video;
    autoplay?: boolean;
    onTimeUpdate?: (e: any) => void;
    isEditPage?: boolean;
    isRelatedVideos?: boolean;
    aspectAwareRef?: RefObject<HTMLVideoElement>;
  },
  ref,
) {
  const theme = useTheme();
  const isSm = useMediaQuery(theme.breakpoints.down("sm"));
  const { enqueueSnackbar } = useSnackbar();
  const hlsRef = useRef<Hls | null>(null);
  const playerRef = useRef<Plyr | null>(null);
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const videoRef = props.aspectAwareRef ?? localVideoRef;
  const videoControl = [
    "play-large",
    "play",
    "current-time",
    "duration",
    "progress",
    "mute",
    "volume",
    "captions",
    "settings",
    "fullscreen",
    "playing",
  ];
  const [loading, setLoading] = useState(false);
  const [videoStartsAt, setVideoStartsAt] = useState<number | null>(null);
  const isSharePage = window.location.href.includes("/shares/");
  const shouldRenderPlayer = props.autoplay || props.isEditPage;
  const baseVideoUrl = isSharePage ? "/shares" : "/api/ikm/v1/videos";
  const currentUrl = `${baseVideoUrl}/${props.video?.id}/hls/${isSharePage ? "public" : ""}/master.m3u8`;
  const AUTO_QUALITY = 0;

  const videoPlayerBoxShadow =
    (theme.palette.mode === "dark" &&
      props.autoplay &&
      "0 120px 400px -180px rgba(255,255,255,.6)") ||
    undefined;

  // Helper to replace "0p" with "auto" in Plyr quality menu (refactored)
  const replaceTextInElements = (
    selector: string,
    from: string,
    to: string,
  ) => {
    document.querySelectorAll(selector).forEach((buttonEl) => {
      if (buttonEl.textContent?.trim() === from) {
        buttonEl.textContent = to;
      }
    });
  };

  const updateQualityUI = () => {
    replaceTextInElements(".plyr__menu__value", "0p", "Auto");
    replaceTextInElements(
      ".plyr__menu__container [role='menuitemradio']",
      "0p",
      "Auto",
    );
  };

  const attachSafariAutoClick = (
    videoRef: { current: HTMLVideoElement },
    hlsRef: { current: Hls },
    playerRef: { current: Plyr },
    setLoading: (v: boolean) => void,
  ) => {
    document
      .querySelectorAll(".plyr__menu__container [role='menuitemradio']")
      .forEach((buttonEl) => {
        if (buttonEl.textContent?.trim() === "Auto") {
          buttonEl.addEventListener("click", () => {
            const player = playerRef.current;
            const currentVideo = videoRef.current;
            if (!player || !hlsRef.current) return;
            const targetTime = currentVideo?.currentTime || 0;
            player.pause();
            setLoading(true);
            hlsRef.current.currentLevel = -1;
            hlsRef.current.once(Hls.Events.FRAG_BUFFERED, () => {
              setLoading(false);
              if (currentVideo) {
                currentVideo.currentTime = targetTime;
                player.play();
              }
            });
            // Trigger Plyr"s quality onChange for Auto
            if (playerRef.current) {
              playerRef.current.quality = AUTO_QUALITY;
              // Immediately update display label
              updateQualityUI();
              // Also explicitly set the quality value text
              const valueEl = document.querySelector(".plyr__menu__value");
              if (valueEl) {
                valueEl.textContent = "Auto";
              }
            }
          });
        }
      });
  };

  const addTimeStampMarkers = (
    videoRef: { current: HTMLVideoElement },
    timeStamps: VideoTimestamp[] | undefined,
    color: string,
  ) => {
    const interval = setInterval(() => {
      const duration = videoRef.current?.duration;
      const progress = document.querySelector(
        ".plyr__progress input[type='range']",
      );
      if (
        duration &&
        progress &&
        !progress.parentElement?.querySelector(".plyr-marker")
      ) {
        timeStamps?.forEach((marker) => {
          const el = document.createElement("div");
          el.className = "plyr-marker";
          el.style.position = "absolute";
          el.style.height = "100%";
          el.style.width = "2px";
          el.style.backgroundColor = color;
          el.style.left = `${(Number(marker.starts_at) / duration) * 100}%`;
          el.style.top = "0";
          el.dataset.text = marker.text;

          progress.parentElement?.appendChild(el);
        });

        // Add mousemove listener to progress bar for marker tooltip override
        const progressBar = document.querySelector(".plyr__progress");

        if (progressBar) {
          progressBar.addEventListener("mousemove", (e: MouseEvent) => {
            const markers = document.querySelectorAll(".plyr-marker");
            const tooltip = document.querySelector(".plyr__tooltip");
            let overMarker = false;

            markers.forEach((marker) => {
              const rect = marker.getBoundingClientRect();
              const mouseX = e.clientX;
              if (mouseX >= rect.left - 2 && mouseX <= rect.right + 2) {
                overMarker = true;
                if (tooltip && marker.dataset.text) {
                  tooltip.textContent = tooltip.textContent + " " + marker.dataset.text
                }
              }
            });
          });
        }
        clearInterval(interval);
      }
    }, 100);
  };

  useImperativeHandle(ref, () => ({
    handleTimeStapmJump: (timeStamp: string) => {
      if (videoRef?.current && parseFloat(timeStamp || "")) {
        videoRef.current.currentTime = parseFloat(timeStamp || "");
      }
    },
    getCurrentTime: () => {
      return videoRef?.current?.currentTime || 0;
    },
  }));

  useLayoutEffect(() => {
    if (!videoRef.current) return;

    if (!Hls.isSupported()) {
      enqueueSnackbar("Video is not supported in this browser", {
        variant: "error",
      });
      return;
    }

    if (Hls.isSupported()) {
      setLoading(true);
      // Configure HLS.js ABR: pick initial level based on throughput and cap to player size
      const hls = new Hls({
        startLevel: -1, // auto-select initial level
        capLevelToPlayerSize: false, // allow full-HD even if player size is smaller
        maxBufferLength: 30, // limit buffer to 30s
      });
      hlsRef.current = hls;
      hls.loadSource(currentUrl);
      hls.attachMedia(videoRef.current!);
      // Hide loading once first fragment is buffered
      const initialFragmentHandler = () => {
        setLoading(false);
        hls.off(Hls.Events.FRAG_BUFFERED, initialFragmentHandler);
      };
      hls.on(Hls.Events.FRAG_BUFFERED, initialFragmentHandler);

      hls.on(Hls.Events.MANIFEST_PARSED, () => {
        const levels = hls.levels;
        const qualities = levels.map((l) => l.height).sort((a, b) => b - a);
        const qualityOptions = [AUTO_QUALITY, ...qualities];

        // Add timestamp markers before Plyr initialization
        if (!isSm) {
          addTimeStampMarkers(
            videoRef,
            props.video.timestamps,
            theme.palette.primary.main,
          );
        }


        // Initialize Plyr only once, at the start
        playerRef.current = new Plyr(videoRef.current!, {
          autoplay: props.autoplay,
          controls: videoControl,
          settings: ["captions", "quality", "speed"],
          hideControls: false,
          invertTime: false,
          speed: {
            selected: 1,
            options: [0.5, 1, 1.25, 1.5, 2],
          },
          quality: {
            default: AUTO_QUALITY,
            options: qualityOptions,
            forced: true,
            onChange: (newQuality) => {
              const player = playerRef.current;
              if (!player) return;

              const isCurrentlyAuto =
                hls.autoLevelEnabled || hls.currentLevel === -1;
              const isTargetAuto = newQuality === AUTO_QUALITY;

              const isSameQuality = (() => {
                if (isTargetAuto && isCurrentlyAuto) {
                  return true;
                }
                if (!isTargetAuto && !isCurrentlyAuto) {
                  const currentLevelHeight = levels[hls.currentLevel]?.height;
                  return currentLevelHeight === newQuality;
                }
                return false;
              })();

              if (isSameQuality) {
                setLoading(false);
                return;
              }

              const targetTime = videoRef.current?.currentTime || 0;
              player.pause();
              setLoading(true);
              // Set the current quality level
              if (isTargetAuto) {
                hls.currentLevel = -1;
              } else {
                const levelIndex = levels.findIndex(
                  (l) => l.height === newQuality,
                );
                if (levelIndex !== -1) {
                  hls.currentLevel = levelIndex;
                }
              }
              // On first buffered fragment after quality change, resume playback
              hls.once(Hls.Events.FRAG_BUFFERED, () => {
                setLoading(false);
                if (videoRef.current) {
                  videoRef.current.currentTime = targetTime;
                  player.play();
                }
              });
            },
          },
        });
        // Set initial quality in Plyr UI to "auto"
        playerRef.current.quality = AUTO_QUALITY;

        // Set border radius for Plyr container
        document
          .querySelectorAll(".plyr")
          .forEach((el) => (el.style.borderRadius = "12px"));

        // Get the "Auto" by default before initialization
        updateQualityUI();

        // After all of the quality change needs to be changed
        playerRef.current.on("qualitychange", () => {
          updateQualityUI();
          if (isSafariBrowser) {
            attachSafariAutoClick(videoRef, hlsRef, playerRef, setLoading);
          }
        });

        // Override Plyr tooltip style for longer text
        const styleEl = document.createElement("style");
        styleEl.textContent = `
          .plyr__tooltip {
            white-space: normal !important;
            max-width: 250px !important;
            word-wrap: break-word !important;
            text-align: center !important;
          }
        `;
        document.head.appendChild(styleEl);

        // Add <track> elements dynamically for subtitles
        if (props.video.subtitles) {
          props.video.subtitles.forEach((c) => {
            const track = document.createElement("track");
            track.kind = "captions";
            track.label = c.label;
            track.srclang = c.language_code;
            track.src = c.src;
            if (c.default) track.default = true;
            videoRef.current?.appendChild(track);
          });

          // Refresh captions in Plyr
          playerRef.current.toggleCaptions(true);
        }
      });
    }

    return () => {
      // Clean up for the upcoming video
      if (playerRef.current) {
        playerRef.current.destroy();
        playerRef.current = null;
      }
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
    };
  }, [props.autoplay, props.video?.id]);

  useEffect(() => {
    const ts = new URLSearchParams(window.location.search).get("ts");
    if (ts) setVideoStartsAt(Number(ts));
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || videoStartsAt == null) return;

    // Wait until metadata is loaded before setting currentTime
    const setTime = () => {
      video.currentTime = videoStartsAt;
    };

    if (video.readyState >= 1) {
      setTime();
    } else {
      video.addEventListener("loadedmetadata", setTime, { once: true });
    }
    // Clean up
    return () => {
      videoRef.current?.removeEventListener("loadedmetadata", setTime);
    };
  }, [videoStartsAt, props.autoplay]);

  return (
    <Box sx={{ position: "relative" }}>
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            borderRadius: "12px",
            width: 1,
            height: 1,
            bgcolor: "rgba(0,0,0,0.3)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 1,
            pointerEvents: "none",
          }}
        >
          <CircularProgress color="primary" />
        </Box>
      )}
      {/* Video itself */}
      {shouldRenderPlayer && props.video?.id && (
        <Box
          key={props.video?.id}
          sx={{
            borderRadius: 3,
            boxShadow: videoPlayerBoxShadow,
          }}
        >
          <video
            ref={videoRef}
            style={
              {
                width: "100%",
                height: "100%",
                borderRadius: 12,
                aspectRatio: "16/9",
                "--plyr-color-main": theme.palette.primary.main,
              } as CSSProperties
            }
          />
        </Box>
      )}

      {props.isRelatedVideos && (
        <Box
          sx={{
            height: 1,
            width: 1,
            paddingBottom: "56.25%",
            bgcolor: theme.palette.divider,
            borderRadius: 3,
            position: "relative",
            overflow: "hidden",
            transition: "border-radius 0.2s ease-in-out",
            cursor: "pointer",
            boxShadow: videoPlayerBoxShadow,
          }}
        >
          {/* Thumbnail image */}
          {!props.autoplay && thumbnailUrl(props.video, "card") && (
            <img
              src={thumbnailUrl(props.video, "card")}
              loading="lazy"
              style={{
                position: "absolute",
                right: 0,
                top: 0,
                width: "100%",
                height: "100%",
                objectFit: "contain",
              }}
            />
          )}
          {/* Video length */}
          {!props.autoplay && props.video.duration_in_seconds && (
            <Box
              sx={{
                position: "absolute",
                right: theme.spacing(1),
                bottom: theme.spacing(1),
                bgcolor: "black",
                borderRadius: 1,
                px: 1,
                py: 0,
              }}
            >
              <Typography variant="body2" sx={{ color: "common.white" }}>
                {secondsToDurationString(props.video.duration_in_seconds)}
              </Typography>
            </Box>
          )}

          {!props.autoplay && <PlayOverlay />}
        </Box>
      )}
    </Box>
  );
});
export default AspectAwareContainer;
